// src/components/UserProfile.js
import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
} from "reactstrap";
import { DataContext } from "../../context/DataContext";
import { Avatar } from "../../components/Avatar";
import { API_BASE } from "../../config/api";

function UserProfile() {
  const { data, setData } = useContext(DataContext);
  const profile = data.userProfile || {};

  const [form, setForm] = useState({
    name: "",
    title: "",
    city: "",
    country: "",
    about: "",
    phone: "",
    image: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      name: profile.name || "",
      title: profile.title || "",
      city: profile.city || "",
      country: profile.country || "",
      about: profile.about || "",
      phone: profile.phone || "",
      image: profile.image || "",
    });
    setPreviewUrl(profile.image || "");
  }, [profile]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  // Convert JPEG to PNG via canvas
  function convertToPng(file) {
    return new Promise((resolve, reject) => {
      if (!file.type.match(/image\/(jpeg|jpg)/)) {
        return resolve(file);
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              const pngFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '') + '.png',
                { type: 'image/png' }
              );
              resolve(pngFile);
            },
            'image/png'
          );
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    convertToPng(file)
      .then((convFile) => {
        setSelectedFile(convFile);
        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result);
        reader.readAsDataURL(convFile);
      })
      .catch((err) => {
        console.error('Image conversion failed', err);
        setError('Failed to process image');
      });
  }

  async function uploadToS3(file) {
    // 1) get pre-signed URL with folder and userId
    const API = API_BASE;
    const res = await fetch(`${API}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileType: file.type,
        folder: 'users',
        userId: String(profile.id)
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Failed to get upload URL: ${res.status} ${errorText}`);
    }
    const { uploadUrl, key } = await res.json();

    // 2) PUT file to S3
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,

      },
      body: file,
    });
    console.log('S3 PUT status:', uploadRes.status, await uploadRes.text());
    if (!uploadRes.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadRes.status}`);
    }

    // 3) derive public URL
    const bucket = 'beveragebrothers';
    return `https://${bucket}.s3.amazonaws.com/${key}`;
  }

async function handleSave() {
  setSaving(true);
  setError('');

  try {
    let updatedImageUrl = form.image;

    if (selectedFile) {
      // 1) upload and get the plain S3 URL
      const plainUrl = await uploadToS3(selectedFile);

      // 2) append a cache-busting query param
      const cacheBustedUrl = `${plainUrl}?cb=${Date.now()}`;

      // 3) immediately update the preview so the UI shows the new image
      setPreviewUrl(cacheBustedUrl);

      // 4) use that in the saved profile
      updatedImageUrl = cacheBustedUrl;
    }

    // now save the profile, including the cache-busted image URL
    const res = await fetch(
      `${API_BASE}/users/${profile.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...profile, ...form, image: updatedImageUrl }),
      }
    );
    if (!res.ok) throw new Error(`Save failed (${res.status})`);
    const updatedProfile = await res.json();
    setData(d => ({ ...d, userProfile: updatedProfile }));

  } catch (err) {
    console.error(err);
    setError(err.message || 'Save failed');
  } finally {
    setSaving(false);
  }
}

  return (
    <div className="content">
      <Row>
        <Col md="4">
          <Card className="card-user">
            <div className="image text-center py-3">
              <label htmlFor="profile-image-input" style={{ cursor: 'pointer' }}>
                <Avatar
                  src={previewUrl}
                  alt={profile.name}
                  size={110}
                  className="avatar border-gray"
                />
                 
              </label>
              
              <Input
                id="profile-image-input"
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              
            </div>
            <div className="text-center">Click to change image</div>
            <CardBody className="text-center">
              <CardTitle tag="h4">{profile.name}</CardTitle>
              <p className="text-muted">{profile.title}</p>
              <p className="text-muted mb-0">{profile.email}</p>
            </CardBody>
          </Card>
        </Col>
        <Col md="8">
          <Card>
            <CardHeader>
              <h5 className="title">Edit Profile</h5>
            </CardHeader>
            <CardBody>
              {error && <div className="text-danger mb-2">{error}</div>}
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Name</label>
                      <Input
                        name="name"
                        value={form.name}
                        placeholder="Name"
                        type="text"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Title</label>
                      <Input
                        name="title"
                        value={form.title}
                        placeholder="Title"
                        type="text"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Email (readonly)</label>
                      <Input
                        value={profile.email}
                        type="email"
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Phone</label>
                      <Input
                        name="phone"
                        value={profile.phone}
                        type="text"
                        placeholder="Phone Number"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>City</label>
                      <Input
                        name="city"
                        value={form.city}
                        placeholder="City"
                        type="text"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label>Country</label>
                      <Input
                        name="country"
                        value={form.country}
                        placeholder="Country"
                        type="text"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <label>About Me</label>
                      <Input
                        className="textarea"
                        type="textarea"
                        rows="4"
                        name="about"
                        value={form.about}
                        placeholder="Tell us about yourself"
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </CardBody>
            <CardFooter>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default UserProfile;
